import { groupBy } from 'lodash'
import { ReactNode, SyntheticEvent, useContext } from 'react'
import UserContext from 'shared/contexts/user'
import CreationTool from 'site/creation-tools/types/creation-tool'
import getSourceFrames from 'site/creation-tools/utils/get-source-frames'
import styled from 'styled-components'
import { MAX_FILE_COUNT } from '../../../../constants'
import { CreationToolsContext } from '../../../../manager'
import { CreationToolSourceFile } from '../../../../types'
import CreationToolError from '../../../../types/error'
import CreationToolGAEvent from '../../../../types/ga-event'
import CreationToolOutputType from '../../../../types/output-type'
import CreationToolWelcomeButton from '../../../../types/welcome-button'
import getUnvalidatedSource from '../../../../utils/get-unvalidated-source'
import { validateSourceByInput } from '../../../../utils/validate-source'
import DropZone from '../../../drop-zone'
import { CreationToolsWelcomeContext } from './../../context'

const Container = styled(DropZone)`
    input[type='file'] {
        cursor: pointer;
        height: 100%;
        left: 0;
        margin: 0;
        opacity: 0;
        padding: 0;
        position: absolute;
        top: 0;
        width: 100%;
    }
`

type Props = {
    children?: ReactNode
    className?: string
    button: CreationToolWelcomeButton
    mimeTypesOverride?: string[]
}

const byFile = (sourceFiles) =>
    groupBy(sourceFiles, (source: CreationToolSourceFile) => {
        if (source.source instanceof File) {
            return source.source.type
        }
        // pretty sure this will never fire but we can catch it in track js
        console.error(`Upload: source.source is not of type File ${source}`)
        return undefined
    })

const CreationToolsWelcomeFileButton = ({ children, className, button, mimeTypesOverride }: Props) => {
    const { tool, setFiles, setSources, sources, trackEvent, isDraggingFile } = useContext(CreationToolsContext)
    const { setError, setErrorSource, setIsLoading } = useContext(CreationToolsWelcomeContext)
    const { user } = useContext(UserContext)

    // for the welcome screen we want this output type
    // but for add more we'll want the context's
    const outputType = CreationToolWelcomeButton.outputType(button)
    const mimeTypes = mimeTypesOverride || CreationToolWelcomeButton.mimeTypes(button, user.isStaff)
    const gradient = CreationTool.gradient(tool)

    let eventCategory // let it be determined by the route

    const sendEvents = (sourceFiles: CreationToolSourceFile[], eventLabel: string) => {
        for (const [key, values] of Object.entries(byFile(sourceFiles))) {
            // only track events for validated files here
            // error events are handled at a lower level for now (e.g. upload/list/index.tsx)
            trackEvent(eventCategory, eventLabel, key, String(values.length))
        }
    }
    const onFileInput = async (e: SyntheticEvent<HTMLInputElement, Event>) => {
        const { files } = e.currentTarget

        // if GifMaker, choose the category based on the output type
        if (tool === CreationTool.GifMaker) {
            eventCategory = CreationToolGAEvent.outputTypeCategory(outputType)
        }

        if (!files || !files.length) return

        // check if too many files
        if (files.length > MAX_FILE_COUNT) {
            setError(CreationToolError.TooManyFiles)
            return
        }

        if (button === CreationToolWelcomeButton.UploadAddMore) {
            const newSources = Array.from(files).map(getUnvalidatedSource)
            setSources([...sources, ...newSources])
            sendEvents(newSources, 'Add to Upload Queue')
        } else {
            const eventLabel = isDraggingFile ? 'Drag And Drop' : 'Browse Files'
            if (files.length === 1) {
                const file = files[0]

                setIsLoading(true)

                try {
                    const requiresFrameProcessing = CreationTool.requiresFrameProcessing(tool)
                    // if the file will be processed into an array of frames, we don't need
                    // to worry about validating audio or transparency. this will automatically
                    // be accounted for via the tool's creation steps. (i.e. Cut Out)
                    const validatedFile = await validateSourceByInput(file, mimeTypes, {
                        requiresAudio: !requiresFrameProcessing && CreationToolOutputType.requiresAudio(outputType),
                        requiresTransparency:
                            !requiresFrameProcessing && CreationToolOutputType.requiresTransparency(outputType),
                    })
                    let processedFile = validatedFile

                    if (requiresFrameProcessing) {
                        const frames = await getSourceFrames({ sourceFile: validatedFile })
                        processedFile.frames = frames
                    }
                    setFiles([processedFile], outputType)
                    trackEvent(eventCategory, eventLabel, (validatedFile.source as File).type, '1')
                } catch (error) {
                    setError(error as CreationToolError)
                    setErrorSource(file)
                    trackEvent(
                        undefined,
                        CreationToolError.gaAction(error as CreationToolError, outputType),
                        CreationToolError.gaLabel(error as CreationToolError, file),
                        CreationToolError.gaValue(error as CreationToolError, file)
                    )
                }

                setIsLoading(false)
            } else {
                const newSources = Array.from(files).map(getUnvalidatedSource)
                sendEvents(newSources, eventLabel)
                setFiles(newSources, outputType)
            }
        }
    }

    return (
        <Container
            {...{
                className,
                gradient,
            }}
        >
            <>
                {children}
                <input
                    type="file"
                    accept={mimeTypes.join(',')}
                    multiple={CreationTool.supportsMultipleFiles(tool)}
                    onInput={onFileInput}
                />
            </>
        </Container>
    )
}

export default CreationToolsWelcomeFileButton
